import { message } from "antd";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";

import EditBankAccount from "./editBankAccount";
import CloseBankAccount from "./closeBankAccount";
import GenericDeleteModal from "../buttons/genericDeleteModal";
import UnassignEntityFromBankAccount from "./modals/unassignEntityFromBankAccount";
import { generateBankAccountDetailsUrl } from "./generateBankAccountDetailsUrl";
import GenericDropdown from "../generic/genericDropdown";
import TableWithLoadingWrapper from "../generic/dataGrid";
import MarkClosed from "../../layout/dashboard/bankAccountsAll/tempComponents/closedAccounts/markClosed";
import UpdateManualAccountBalance from "./updateManualAccountBalance";
import GenericTagChips from "../specific/customTags/genericTagChips";
import { toPrice } from "../../helpers/stringFormat";
import GenericLink from "../generic/genericLink";
import BankAccountPaymentsApplicationState from '../bankAccount/BankAccountPaymentsApplicationState';
import checkFlag from "../../helpers/featureFlags";
import PermissionGate from '../../helpers/PermissionGate';

// Component for rendering the description cell
const DescriptionCell = ({ record }) => {
  let accountDescription = record.account.account_description;
  const last4 = record.account.account_number_last_4;

  // Add last 4 digits to description if available and not already included
  if (last4 && last4.length > 3 && last4.length < 7 && !accountDescription.includes(last4)) {
    accountDescription = `${accountDescription} (...${last4})`;
  }

  return (
    <Box>
      {!record.account.marked_closed ? (
        <GenericLink
          toURL={generateBankAccountDetailsUrl(
            undefined, // ownerName
            record.id,
            undefined, // ownerId
            record.purpose.name,
          )}
          onClick={() => {
            localStorage.setItem("account_balance", record.account.aggregated_balance_raw);
            localStorage.setItem("account_name", record.account.account_description);
          }}
          text={record.description}
        />
      ) : (
        <>
          {record.account.marked_closed && (
            <Chip
              label="Closed"
              size="small"
              color="default"
              sx={{ marginRight: 1, borderRadius: 0 }}
            />
          )}
          <Typography variant="primary" color="textSecondary">
            {record.description}
          </Typography>
        </>
      )}
    </Box>
  );
};

const ActionsCell = ({
  record,
  allowActions,
  reassignBankAccount,
  editBankAccount,
  deleteBankAccount,
  setAccounts,
  updateBalanceSuccess,
  reportsPage,
  refreshExternalConnection
}) => {
  if (!allowActions) return null;

  const dropdownItems = reportsPage
    ? [
      (record?.account?.account_source === "PL" || record?.account?.account_source === "FI")
        ? (<a onClick={() => refreshExternalConnection(record)}> Force Refresh </a>) : undefined
    ]
    : [
      <EditBankAccount
        source={record.account.account_source}
        accountId={record.id}
        accountPurposeName={record.purpose.name}
        accountPurposeId={record.purpose.id}
        accountGenericTags={record.generic_tags}
        institutionName={record.account.bank?.name}
        accountDescription={record.description}
        entityName={record.entity_name}
        onSuccessReassign={reassignBankAccount}
        onSuccessRename={editBankAccount}
      />,
      record.account.account_source === "UN" ? (
        <CloseBankAccount
          buttonView={false}
          bankAccountId={record.id}
          bankInfo={record}
        />
      ) : undefined,
      record.entity_name && record.account.account_source !== "UN" ? (
        <UnassignEntityFromBankAccount
          bankAccount={record}
          unassignEntityFromBankAccount={deleteBankAccount}
        />
      ) : undefined,
      <MarkClosed
        bankAccountId={record.id}
        markedClosed={record.account.marked_closed}
        setAccounts={setAccounts}
        record={record}
      />,
      record.account.account_source === "MA" ? (
        <a>
          <UpdateManualAccountBalance
            button={'Update Balance'}
            bankAccountInfo={record}
            balance={record.account.current_balance_raw}
            bankId={record.id}
            updateBalanceSuccess={updateBalanceSuccess}
          />
        </a>
      ) : undefined,
      <GenericDeleteModal
        type='Bank Account'
        title={`Are you sure you want to delete 
              ${record.description === record.account.account_description ?
            record.description : (record.description + " - " + record.account.account_description)}?`}
        URL='/api/bank-account/'
        deleteParams={{ 'account_id': record.id, 'purpose_id': record.purpose.id }}
        description={`By clicking Delete below you will no longer have the bank account ${record.description === record.account.account_description ?
          record.description : (record.description + " - " + record.account.account_description)} on your dashboard. If you do not want to proceed or unsure, please click X on the upper right.`}
        handleDeleteSuccess={() => deleteBankAccount(record.id, record.purpose.id)}
        handleDeleteFailure={() => message.error(`Failed to delete the bank account ${record.account.account_description}`)}
      />
    ];

  return (
    <PermissionGate requiredRole='dashboardEditor'>
      <GenericDropdown dropdownItems={dropdownItems.filter(Boolean)} />
    </PermissionGate>
  );
};

// Component for rendering timestamp cells on reports page
const TimeCell = ({ timestamp }) => (
  <Box sx={{ mt: 2 }}>
    {timestamp ? (
      <Typography>
        {new Date(timestamp).toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }).replace(",", " at")}
      </Typography>
    ) : (
      <Typography fontStyle="italic" color="text.disabled">
        Unknown
      </Typography>
    )}
  </Box>
);

const BankAccountMultiView = ({
  accountsInfo = [],
  loadingTable = false,
  reportsPage = false,
  allowActions = true,
  reassignBankAccount,
  editBankAccount,
  deleteBankAccount,
  setAccounts,
  updateBalanceSuccess,
  displayPaymentsVerifiedColumn = false,
  entityPaymentsVerified,
  setSelectedBankAccount,
  setFundingSourceApplicationVisible,
  setVerifyMicroDepsVisible,
  entityId,
  refreshExternalConnection
}) => {

  // Common columns for both views
  const commonColumns = [
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      renderCell: (params) => <DescriptionCell record={params.row} />,
      sortComparator: (a, b, aRow, bRow) => {
        const aClosed = aRow?.account?.marked_closed ?? false;
        const bClosed = bRow?.account?.marked_closed ?? false;
        if (aClosed !== bClosed) {
          return aClosed ? 1 : -1;
        }
        return a?.localeCompare(b);
      }
    },
    {
      field: "tags",
      headerName: "Placement",
      flex: 2.5,
      renderCell: (params) => (
        <GenericTagChips
          genericTags={params.row.generic_tags || []}
          purpose={params.row.purpose}
          bank={params.row.account?.bank?.name}
          beneficiary={params.row.entity_name}
        />
      ),
    },
  ];

  // Reports page specific columns
  const reportsPageColumns = [
    {
      field: "attempt_time",
      headerName: "Last Attempt",
      flex: 1,
      renderCell: (params) => (
        <TimeCell timestamp={params.row.account?.source_account?.last_attempt} />
      ),
    },
    {
      field: "balance_time",
      headerName: "Balance Time",
      flex: 1,
      renderCell: (params) => (
        <TimeCell timestamp={params.row.account?.source_account?.last_api_aggregation_success} />
      ),
    },
    {
      field: "transaction_time",
      headerName: "Transaction Time",
      flex: 1,
      renderCell: (params) => (
        <TimeCell timestamp={params.row.account?.source_account?.last_expected_transaction} />
      ),
    },
  ];

  // Actions column - used in both views but with different content
  const actionsColumn = {
    field: "actions",
    headerName: "",
    flex: 0.25,
    align: "right",
    renderCell: (params) => (
      <ActionsCell
        record={params.row}
        allowActions={allowActions}
        reassignBankAccount={reassignBankAccount}
        editBankAccount={editBankAccount}
        deleteBankAccount={deleteBankAccount}
        setAccounts={setAccounts}
        updateBalanceSuccess={updateBalanceSuccess}
        reportsPage={reportsPage}
        refreshExternalConnection={refreshExternalConnection}
      />
    ),
  };

  // Regular view specific columns
  const balanceColumn = {
    field: "account",
    headerName: "Balance",
    headerAlign: "right",
    flex: 1,
    align: "right",
    renderCell: (params) => (
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          justifyContent: "center",
          alignItems: "right",
          height: '100%'
        }}>
        <Typography align="right">
          {toPrice(params.row.account.aggregated_balance_raw)}
        </Typography>
      </Box>
    ),
    sortComparator: (a, b) => {
      return a?.aggregated_balance_raw - b?.aggregated_balance_raw;
    }
  };

  // Build the columns array based on the page type
  let columns = [...commonColumns];

  // Add page-specific columns
  if (reportsPage) {
    columns = [...columns, ...reportsPageColumns, actionsColumn];
  } else {
    columns = [...columns, balanceColumn, actionsColumn];
  }

  // Conditionally add payments column if needed
  if (checkFlag('paymentsV2') && displayPaymentsVerifiedColumn && !reportsPage) {
    const paymentsCol = {
      field: "payments_enabled",
      headerName: "Payments Enabled",
      flex: 1,
      renderCell: (params) => {
        const {
          id,
          dwolla_funding_source_id,
          dwolla_funding_source_is_verified
        } = params.row;
        const bankAccountSource = params.row.account.account_source;
        const bankAccountDescription = params.row.account.account_description;

        return (
          <BankAccountPaymentsApplicationState
            bankAccountId={id}
            bankAccountPaymentsId={dwolla_funding_source_id}
            bankAccountIsVerified={dwolla_funding_source_is_verified}
            entityPaymentsVerified={entityPaymentsVerified}
            setSelectedBankAccount={setSelectedBankAccount}
            setApplicationVisible={setFundingSourceApplicationVisible}
            setVerifyMicroDepsVisible={setVerifyMicroDepsVisible}
            bankAccountSource={bankAccountSource}
            bankAccountDescription={bankAccountDescription}
            entityId={entityId}
          />
        );
      },
    };
    // Insert payments column before the Balance column
    columns.splice(columns.length - 2, 0, paymentsCol);
  }

  return (
    <TableWithLoadingWrapper
      columns={columns}
      pagination={{
        paginationModel: { pageSize: Math.min(accountsInfo?.length || 50, 50), page: 0 },
      }}
      pageSizeOptions={[25, 50, 100]}
      optInRowHeightAuto={true}
      dataSource={accountsInfo || []}
      loading={loadingTable || false}
      sortModel={[{ field: 'description', sort: 'asc' }]}
    />
  );
};

export default BankAccountMultiView;